import React, { useEffect, useState } from "react";
import { ApiPaths } from "../../Config/ApiPath";
import PaginationComponent from "./../../Components/PaginationControls/PaginationControls";
import useAxiosHelper from "../../Common/AxiosHelper";
import { BasicInfo } from "../../Config/BasicInfo";
import moment from "moment";
import "../Claim/ClaimApp.css";
import {
  ArrowLeft,
  ArrowUpRight,
  SlidersHorizontal,
  X,
  RotateCcw,
  Search,
  Inbox,
  ExternalLink,
  Calendar,
  Copy,
  Check,
} from "lucide-react";

const statusOf = (s) =>
  s == "0" ? { key: "wait", label: "Pending" } : s == "1" ? { key: "ok", label: "Success" } : { key: "bad", label: "Rejected" };

const usd = (v) =>
  `$${(parseFloat(v) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ClaimedHistory = ({ refresh, onBack }) => {
  const [filterVisiblity, setFilterVisiblity] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paymentTransaction, setPaymentTransaction] = useState();
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [limitFilter, setLimitFilter] = useState(10);
  const { AxiosGet } = useAxiosHelper();
  const [withdrawType] = useState("withdrawal");
  const [search, setSearch] = useState({ startDate: "", endDate: "" });
  const [draft, setDraft] = useState({ startDate: "", endDate: "" });
  const [copiedHash, setCopiedHash] = useState("");

  const handlePagination = (page) => {
    setCurrentPage(page);
  };

  const FetchData = async (page = currentPage, s = search, limit = limitFilter) => {
    try {
      setLoading(true);
      const filteredSearch = Object.fromEntries(Object.entries(s).filter(([, value]) => value !== ""));
      const queryParams = { page, limit, source: withdrawType, ...filteredSearch };
      const response = await AxiosGet(`${ApiPaths.getPaymentTransaction}?${new URLSearchParams(queryParams).toString()}`);
      BasicInfo.isDebug && console.log("withdraw history", response);
      setPaymentTransaction(response?.data || []);
      setTotalPages(response?.totalPages || 1);
    } catch (error) {
      console.error("Error fetching payment transactions:", error);
      setPaymentTransaction((p) => p || []);
    } finally {
      setLoading(false);
    }
  };

  // initial load, parent refresh (after a new withdrawal) and paging
  useEffect(() => {
    FetchData(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh, currentPage, limitFilter]);

  const applyFilter = () => {
    setSearch(draft);
    setCurrentPage(1);
    setFilterVisiblity(false);
    FetchData(1, draft);
  };

  function resetData() {
    const empty = { startDate: "", endDate: "" };
    setSearch(empty);
    setDraft(empty);
    setCurrentPage(1);
    setLimitFilter(10);
    setFilterVisiblity(false);
    FetchData(1, empty, 10);
  }

  // filter sheet: Escape closes, page behind stays put
  useEffect(() => {
    if (!filterVisiblity) return undefined;
    const onKey = (e) => e.key === "Escape" && setFilterVisiblity(false);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [filterVisiblity]);

  const copyHash = async (h) => {
    try {
      await navigator.clipboard.writeText(h);
      setCopiedHash(h);
      setTimeout(() => setCopiedHash(""), 1400);
    } catch (e) {
      BasicInfo.isDebug && console.log(e);
    }
  };

  const list = Array.isArray(paymentTransaction) ? paymentTransaction : [];
  const firstLoad = paymentTransaction === undefined;
  const datesOn = Boolean(search.startDate || search.endDate);
  const filtersOn = datesOn || Number(limitFilter) !== 10;
  const pageReceived = list.filter((x) => x?.status == "1").reduce((s, x) => s + (parseFloat(x?.withdrawal_amount) || 0), 0);
  const pagePending = list.filter((x) => x?.status == "0").reduce((s, x) => s + (parseFloat(x?.amount) || 0), 0);

  return (
    <section className="wdHistory">
      <div className="wdHistBar">
        {onBack && (
          <button type="button" className="wdBack" onClick={onBack} aria-label="Back to withdraw">
            <ArrowLeft size={19} />
          </button>
        )}
        <h2>Withdrawal history</h2>
        <button
          type="button"
          className={`wdFilterBtn ${filtersOn ? "isOn" : ""}`}
          onClick={() => {
            setDraft(search);
            setFilterVisiblity(true);
          }}
          aria-label="Filter"
        >
          <SlidersHorizontal size={18} />
          {filtersOn && <i />}
        </button>
      </div>

      {list.length > 0 && (
        <div className="wdHistStats">
          <div>
            <small>Received{totalPages > 1 ? " (this page)" : ""}</small>
            <b className="pos">{usd(pageReceived)}</b>
          </div>
          <div>
            <small>Pending{totalPages > 1 ? " (this page)" : ""}</small>
            <b className="wait">{usd(pagePending)}</b>
          </div>
        </div>
      )}

      {datesOn && (
        <div className="wdActiveFilter">
          <Calendar size={13} />
          {search.startDate ? moment(search.startDate).format("DD MMM YYYY") : "Start"} –{" "}
          {search.endDate ? moment(search.endDate).format("DD MMM YYYY") : "Today"}
          <button type="button" onClick={resetData} aria-label="Clear date filter">
            <X size={13} />
          </button>
        </div>
      )}

      {firstLoad || (loading && !list.length) ? (
        <div className="wdList">
          {[0, 1, 2].map((i) => (
            <div key={i} className="wdTx wdSkel">
              <span className="a" />
              <span className="b" />
            </div>
          ))}
        </div>
      ) : list.length === 0 ? (
        <div className="wdEmpty">
          <span>
            <Inbox size={26} />
          </span>
          <b>No withdrawals yet</b>
          <small>{datesOn ? "Nothing in this date range." : "Your withdrawal requests will appear here."}</small>
          {datesOn && (
            <button type="button" onClick={resetData}>
              <RotateCcw size={14} /> Clear filter
            </button>
          )}
        </div>
      ) : (
        <div className={`wdList ${loading ? "isBusy" : ""}`}>
          {list.map((x, i) => {
            const st = statusOf(x?.status);
            const rejected = x?.status == "2";
            // fee is stored as tx_charge (service tax); older rows may only have TDS
            const fee = parseFloat(x?.tx_charge ?? x?.TDS) || 0;
            return (
              <article key={x?._id || i} className={`wdTx st-${st.key}`}>
                <div className="wdTxTop">
                  <span className={`wdTxIcon ${st.key}`}>
                    <ArrowUpRight size={18} />
                  </span>
                  <span className="wdTxMain">
                    <b>{usd(x?.amount)}</b>
                    <small>
                      {moment(x?.time).format("DD MMM YYYY, hh:mm A")} · #{(currentPage - 1) * limitFilter + (i + 1)}
                    </small>
                  </span>
                  <span className={`wdStatus ${st.key}`}>{st.label}</span>
                </div>

                <dl className="wdTxRows">
                  <div>
                    <dt>Service fee</dt>
                    <dd>{rejected ? "—" : usd(fee)}</dd>
                  </div>
                  <div>
                    <dt>{rejected ? "Returned" : "Amount received"}</dt>
                    <dd className={rejected ? "bad" : "pos"}>{rejected ? usd(x?.amount) : usd(x?.withdrawal_amount)}</dd>
                  </div>
                  {x?.tx_hash && (
                    <div>
                      <dt>Tx hash</dt>
                      <dd className="wdHash">
                        <a href={`https://bscscan.com/tx/${x.tx_hash}`} target="_blank" rel="noopener noreferrer" title={x.tx_hash}>
                          {x.tx_hash.length > 16 ? `${x.tx_hash.slice(0, 8)}…${x.tx_hash.slice(-6)}` : x.tx_hash}
                          <ExternalLink size={12} />
                        </a>
                        <button type="button" onClick={() => copyHash(x.tx_hash)} aria-label="Copy transaction hash">
                          {copiedHash === x.tx_hash ? <Check size={13} /> : <Copy size={13} />}
                        </button>
                      </dd>
                    </div>
                  )}
                </dl>
              </article>
            );
          })}
        </div>
      )}

      {list.length > 0 && (
        <div className="wdPager">
          <PaginationComponent currentPage={currentPage} totalPages={totalPages} onPageChange={handlePagination} />
        </div>
      )}

      {filterVisiblity && (
        <div className="wdOverlay" onClick={() => setFilterVisiblity(false)}>
          <div className="wdSheet" role="dialog" aria-modal="true" aria-labelledby="wdHistFilter" onClick={(e) => e.stopPropagation()}>
            <span className="wdHandle" aria-hidden="true" />
            <button type="button" className="wdClose" onClick={() => setFilterVisiblity(false)} aria-label="Close">
              <X size={18} />
            </button>
            <h3 id="wdHistFilter">Filter withdrawals</h3>
            <div className="wdFieldRow">
              <label className="wdField">
                <span>From</span>
                <input type="date" value={draft.startDate} onChange={(e) => setDraft((d) => ({ ...d, startDate: e.target.value }))} />
              </label>
              <label className="wdField">
                <span>To</span>
                <input type="date" value={draft.endDate} onChange={(e) => setDraft((d) => ({ ...d, endDate: e.target.value }))} />
              </label>
            </div>
            <div className="wdField">
              <span>Per page</span>
              <div className="wdSeg light">
                {[10, 20, 50, 100, 200].map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={Number(limitFilter) === n ? "isOn" : ""}
                    onClick={() => {
                      setLimitFilter(n);
                      setCurrentPage(1);
                    }}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
            <div className="wdSheetActions">
              <button type="button" className="wdBtn ghost" onClick={resetData}>
                <RotateCcw size={16} /> Reset
              </button>
              <button type="button" className="wdBtn primary" onClick={applyFilter}>
                <Search size={16} /> Show results
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default ClaimedHistory;
